// Pill del estado del expediente MDM, sin íconos: la etiqueta y el tono salen de
// `getStatusPresentation` y de la fase del candado.
import { cn } from '@/lib/cn';
import {
    ACTIVE_UNCONFIRMED_LABEL,
    getStatusPresentation,
    isActiveUnconfirmed,
    lockPhaseLabel,
    MDM_LOCK_PHASE_SHORT_LABEL,
    type MdmLockPhase,
} from '../logic';
import type { MdmStatusTone } from '../types';
import type { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';

const TONE_CLASS: Record<MdmStatusTone, string> = {
    success: 'border-success/40 bg-success/10 text-success',
    danger: 'border-destructive/40 bg-destructive/10 text-destructive',
    warning: 'border-warning/40 bg-warning/10 text-warning',
    info: 'border-info/40 bg-info/10 text-info',
    neutral: 'border-border bg-muted text-muted-foreground',
};

const SIZE_CLASS = {
    sm: 'gap-[3px] px-[7px] py-px text-[9.5px]',
    md: 'gap-1 px-2 py-0.5 text-[11px]',
} as const;

export type MdmStatusPillSize = keyof typeof SIZE_CLASS;

/** El tono de cada fase del candado; `locked` conserva el del estado del expediente. */
const PHASE_TONE: Record<MdmLockPhase, MdmStatusTone> = {
    refuted: 'danger',
    scheduled: 'warning',
    unconfirmed: 'warning',
    locked: 'danger',
    cutoffPassed: 'warning',
    cutoffRefuted: 'danger',
};

export interface MdmStatusPillProps {
    status: LoanDeviceMdmStatusEnum;
    size?: MdmStatusPillSize;
    /** Columna angosta: pinta la etiqueta corta y deja la larga en el tooltip. */
    compact?: boolean;
    /**
     * En qué punto del candado está el equipo, de `readLockPhase`. Fuera de `locked` la píldora deja
     * de decir «Bloqueado» a secas: ahí el equipo todavía no está apagado o no lo confirmó nadie.
     */
    lockPhase?: MdmLockPhase | null;
    /** Si el proveedor confirmó la última orden. Obligatorio: sin él un ACTIVE se pintaría libre. */
    mdmConfirmed: boolean;
    /** La hora a la que cae el corte programado; acompaña a `lockPhase: 'scheduled'`. */
    lockScheduledFor?: string;
    className?: string;
}

export function MdmStatusPill({
    status,
    size = 'sm',
    compact = false,
    lockPhase = null,
    mdmConfirmed,
    lockScheduledFor,
    className,
}: MdmStatusPillProps) {
    const base = getStatusPresentation(status);
    const presentation = isActiveUnconfirmed({ mdmStatus: status, mdmConfirmed })
        ? {
              ...base,
              label: ACTIVE_UNCONFIRMED_LABEL,
              shortLabel: ACTIVE_UNCONFIRMED_LABEL,
              tone: 'warning' as const,
          }
        : base;
    const phase = lockPhase === 'locked' ? null : lockPhase;
    const label = phase ? lockPhaseLabel(phase, lockScheduledFor) : presentation.label;
    const shortLabel = phase ? MDM_LOCK_PHASE_SHORT_LABEL[phase] : presentation.shortLabel;
    const tone = phase ? PHASE_TONE[phase] : presentation.tone;
    return (
        <span
            title={compact && shortLabel !== label ? label : undefined}
            className={cn(
                'inline-flex items-center whitespace-nowrap rounded-full border font-semibold',
                TONE_CLASS[tone],
                SIZE_CLASS[size],
                className,
            )}
        >
            {compact ? shortLabel : label}
        </span>
    );
}
